import { useQueryClient } from '@tanstack/react-query';
import { Button, Loader, Stack, toast } from 'component-library';
import { ErrorBoundary } from 'react-error-boundary';
import { Control, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { usePatchJobSubscription } from '@/api/rest/resources/jobSubscriptions';
import { Form } from '@/components';
import { SuspenseBoundary } from '@/components/SuspenseBoundary';
import { JOB_SUBSCRIPTIONS_QUERY_KEY, useJobSubscription } from '@/hooks/useJobSubscriptions';
import { usePlotPortfolioSubscriptions } from '@/hooks/usePlotPortfolioSubscriptions';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import {
  IndicatorProductSubscriptionFormFields,
  IndicatorProductSubscriptionFormValues,
} from '../components/IndicatorProductSubscriptionFormFields';
import { JobSubscriptionFormFields, JobSubscriptionFormValues } from '../components/JobSubscriptionFormFields';
import { SubscriptionStatusFormField } from '../components/SubscriptionStatusFormField';

export const EditJobSubscriptionPage = () => {
  const { t } = useTranslation();
  const { organizationId, jobSubscriptionId } = useParams();

  if (isNullish(organizationId) || isNullish(jobSubscriptionId)) {
    throw new Error('Missing organizationId or jobSubscriptionId route param');
  }

  return (
    <SignedInPageLayout
      items={[]}
      withCustomContentContainer
      showBreadcrumbs={false}
      navBarProps={{
        title: t('shared.ndiOrganizationSubscriptions.editPage.title'),
        backPath: buildPath(paths.staff.organizationNdiSubscriptions, { pathParams: { organizationId } }),
        showSearchBar: false,
      }}
    >
      <Stack className='px-48 py-12 gap-6 max-w-[720px]'>
        <ErrorBoundary fallback={null}>
          <SuspenseBoundary
            fallback={
              <Stack centerMain>
                <Loader />
              </Stack>
            }
          >
            <EditJobSubscriptionForm organizationId={organizationId} jobSubscriptionId={jobSubscriptionId} />
          </SuspenseBoundary>
        </ErrorBoundary>
      </Stack>
    </SignedInPageLayout>
  );
};

type EditJobSubscriptionFormValues = JobSubscriptionFormValues & {
  status: string;
  indicator_product_subscription: {
    calculation_enabled: boolean;
    use_geomodel_service_for_facts: boolean;
    sync_plots_with_geomodel_service: boolean;
    sync_data_for_plots: boolean;
  };
};

const EditJobSubscriptionForm = ({
  organizationId,
  jobSubscriptionId,
}: {
  organizationId: string;
  jobSubscriptionId: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: jobSubscription } = useJobSubscription(jobSubscriptionId);
  const indicatorProductSubscription = jobSubscription.indicator_product_subscription_details;
  const indicatorProductDetails = jobSubscription.indicator_product_details;
  const patchMutation = usePatchJobSubscription();
  const { data: plotPortfolioSubscriptionsData } = usePlotPortfolioSubscriptions({ org: organizationId });
  const plotPortfolioSubscriptions = plotPortfolioSubscriptionsData.results;

  const form = useForm<EditJobSubscriptionFormValues>({
    defaultValues: {
      ndi_plot_portfolio_subscription: jobSubscription.ndi_plot_portfolio_subscription,
      display_name: jobSubscription.display_name,
      start_date: jobSubscription.start_date.slice(0, 10),
      end_date: jobSubscription.end_date.slice(0, 10),
      status: jobSubscription.status,
      job_creation_cost_credits: String(jobSubscription.job_creation_cost_credits),
      job_execution_cost_credits: String(jobSubscription.job_execution_cost_credits),
      indicator_product_subscription: {
        calculation_enabled: indicatorProductSubscription?.calculation_enabled ?? false,
        use_geomodel_service_for_facts: indicatorProductSubscription?.use_geomodel_service_for_facts ?? false,
        sync_plots_with_geomodel_service: indicatorProductSubscription?.sync_plots_with_geomodel_service ?? false,
        sync_data_for_plots: indicatorProductSubscription?.sync_data_for_plots ?? false,
      },
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await patchMutation.mutateAsync({
        pathVariables: { id: jobSubscriptionId },
        bodyData: {
          ndi_plot_portfolio_subscription: values.ndi_plot_portfolio_subscription,
          display_name: values.display_name,
          start_date: values.start_date,
          end_date: values.end_date,
          status: values.status,
          job_creation_cost_credits: Number(values.job_creation_cost_credits),
          job_execution_cost_credits: Number(values.job_execution_cost_credits),
          ...(indicatorProductSubscription && {
            indicator_product_subscription: values.indicator_product_subscription,
          }),
        },
      });

      queryClient.invalidateQueries({ queryKey: [JOB_SUBSCRIPTIONS_QUERY_KEY] });
      toast({ type: 'success', title: t('shared.ndiOrganizationSubscriptions.toast.updateSuccess') });
      navigate(buildPath(paths.staff.organizationNdiSubscriptions, { pathParams: { organizationId } }));
    } catch {
      toast({ type: 'error', title: t('shared.ndiOrganizationSubscriptions.toast.saveError') });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className='flex flex-col gap-8'>
        <Stack spacing={4} className='p-6 rounded-2xl bg-white-100 border border-neutral-black-12'>
          <span className='typography-h4'>
            {t('shared.ndiOrganizationSubscriptions.editPage.sections.subscription')}
          </span>
          <JobSubscriptionFormFields
            control={form.control as unknown as Control<JobSubscriptionFormValues>}
            plotPortfolioSubscriptions={plotPortfolioSubscriptions}
          />
          <SubscriptionStatusFormField control={form.control} />
        </Stack>

        {indicatorProductSubscription && (
          <Stack spacing={4} className='p-6 rounded-2xl bg-white-100 border border-neutral-black-12'>
            <span className='typography-h4'>
              {t('shared.ndiOrganizationSubscriptions.editPage.sections.indicatorProduct')}
            </span>
            <IndicatorProductSubscriptionFormFields
              form={form as unknown as UseFormReturn<IndicatorProductSubscriptionFormValues>}
              countOfPlotsSynced={indicatorProductSubscription.count_of_plots_synced}
            />
          </Stack>
        )}

        {indicatorProductDetails && (
          <Stack spacing={4} className='p-6 rounded-2xl bg-white-100 border border-neutral-black-12'>
            <span className='typography-h4'>
              {t('shared.ndiOrganizationSubscriptions.editPage.sections.indicatorProductDetails')}
            </span>
            <ReadOnlyField
              label={t('shared.ndiOrganizationSubscriptions.editPage.fields.geoModelName')}
              value={indicatorProductDetails.geomodel_geo_model_name}
            />
            <ReadOnlyField
              label={t('shared.ndiOrganizationSubscriptions.editPage.fields.geoModelGitSha')}
              value={indicatorProductDetails.geomodel_git_sha}
            />
            <ReadOnlyField
              label={t('shared.ndiOrganizationSubscriptions.editPage.fields.geoModelVersionNumber')}
              value={indicatorProductDetails.geomodel_version_number}
            />
            <ReadOnlyField
              label={t('shared.ndiOrganizationSubscriptions.editPage.fields.indicatorNames')}
              value={indicatorProductDetails.indicator_names.join(', ')}
            />
            <ReadOnlyField
              label={t('shared.ndiOrganizationSubscriptions.editPage.fields.applicableLandTypes')}
              value={indicatorProductDetails.applicable_land_types.join(', ')}
            />
            <ReadOnlyField
              label={t('shared.ndiOrganizationSubscriptions.editPage.fields.applicableCropTypes')}
              value={indicatorProductDetails.applicable_crop_types.join(', ')}
            />
          </Stack>
        )}

        <Button
          type='submit'
          className='self-end w-fit px-6'
          loading={patchMutation.isPending}
          disabled={patchMutation.isPending}
        >
          {t('global.ui.buttons.save')}
        </Button>
      </form>
    </Form>
  );
};

const ReadOnlyField = ({ label, value }: { label: string; value: string | null }) => (
  <Stack spacing={1}>
    <span className='typography-caption text-text-secondary'>{label}</span>
    <span className='typography-body1'>{value || '-'}</span>
  </Stack>
);
